import {Checkbox, Divider, Stack, Typography} from '@mui/material';
import {CTAButtonFixed, MainFooter, MainHeader, PageWrapperLayout} from 'presentation/components';
import {ModalError} from 'presentation/components/ModalError';
import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';
import {useMediaQueries} from '../hooks';
import {Loading} from './components/Loading';
import ProviderCard from './components/ProviderCard';
import {EntryPointCodeEnum} from './constants/entryPointCodeEnum';
import {ProviderEnum} from './constants/providerEnum';
import {useCommunications} from './hooks/useCommunications';
import {CustomCTAButtonFixedAction, StyledBox, StyledCheckbox, StyledContainer} from './styles';
import LoadingSkeleton from './components/LoadingSkeleton';
import {AlterationType} from './constants/AlterationType';

const {
	title,
	communication,
	changeInServiceNetwork,
	agreeCheckbox,
	networkAccessInfo,
	errorModalDescription,
} = translations['pt-br'].privateFlow.communications;

const {confirm, required, notice, tryAgain, goToHomePage, unableToContinue} =
	translations['pt-br'].commons;

export function Communications() {
	const {isMobile} = useMediaQueries();

	const {
		currentCommunicationIndexState,
		isCheckboxRequiredState,
		transformProvider,
		termsCheckedState,
		setTermsCheckedState,
		goToInitialPage,
		handleConfirmCommunication,
		confirmCommunicationMutation: {isError, isPending},
		communicationsResponse,
		getCommunicationsQuery: {isLoading},
		handleRefreshCommunicationsState,
		currentCommunication,
		checkboxColor,
	} = useCommunications();

	if (isPending) {
		return <Loading />;
	}

	return (
		<PageWrapperLayout title={title}>
			<MainHeader
				hideBackBtn
				hideClose
				variant="subRoute"
				title={
					communicationsResponse.length > 1
						? `${communication} ${currentCommunicationIndexState + 1} de ${communicationsResponse.length}`
						: title
				}
			/>
			<StyledContainer>
				{isLoading || communicationsResponse.length === 0 ? (
					<LoadingSkeleton />
				) : (
					<Fragment>
						<Typography variant="body1" color="text.secondary" fontWeight={600}>
							{changeInServiceNetwork}
						</Typography>
						{currentCommunication ? (
							<Stack spacing={2}>
								<ProviderCard
									provider={transformProvider(
										currentCommunication,
										ProviderEnum.CURRENT_PROVIDER,
									)}
								/>
								{currentCommunication?.prestadorSubstituto ? (
									<ProviderCard
										provider={transformProvider(
											currentCommunication,
											ProviderEnum.NEW_PROVIDER,
										)}
									/>
								) : null}
							</Stack>
						) : null}
						<Divider />
						<Stack spacing={2}>
							<Typography variant="caption" color="text.secondary">
								{networkAccessInfo.paragraph1}
							</Typography>
							{currentCommunication?.cdTipoAltSubst ===
							AlterationType.REDUCTION_WITH_IMPACT ? (
								<Typography variant="caption" color="text.secondary">
									{networkAccessInfo.paragraph2}
								</Typography>
							) : null}
							<Typography variant="caption" color="text.secondary">
								{networkAccessInfo.paragraph3}
							</Typography>
						</Stack>

						<CTAButtonFixed.Root
							direction={'column'}
							justifyContent={'end'}
							mt={isMobile ? 2 : 0}
							sx={{bottom: 0}}>
							<StyledCheckbox
								control={
									<Checkbox
										checked={termsCheckedState}
										onChange={(_, checked) => setTermsCheckedState(checked)}
										sx={{
											color: checkboxColor,
										}}
									/>
								}
								label={
									<Stack>
										<Typography variant="body2" color={checkboxColor}>
											{agreeCheckbox}
										</Typography>
										{isCheckboxRequiredState ? (
											<Typography variant="helperText" color={checkboxColor}>
												{required}
											</Typography>
										) : null}
									</Stack>
								}
							/>
							<CustomCTAButtonFixedAction
								fullWidth={!!isMobile}
								onClick={() =>
									handleConfirmCommunication({
										communitation: currentCommunication?.cdComunicado,
										entryPointCode: EntryPointCodeEnum.V1,
									})
								}
								size="large"
								variant="contained"
								sx={{px: 7.5}}>
								{confirm}
							</CustomCTAButtonFixedAction>
						</CTAButtonFixed.Root>
					</Fragment>
				)}
			</StyledContainer>

			<ModalError
				headerTitle={notice}
				title={unableToContinue}
				description={errorModalDescription}
				open={isError}
				maxWidth={420}
				isFullScreen={false}
				disableClose={true}
				firstButton={{
					title: tryAgain,
					handleClick: () =>
						handleConfirmCommunication({
							communitation: currentCommunication?.cdComunicado,
							entryPointCode: EntryPointCodeEnum.V1,
						}),
				}}
				secondButton={{
					title: goToHomePage,
					handleClick: () => {
						handleRefreshCommunicationsState();
						goToInitialPage();
					},
				}}
			/>

			<StyledBox mt="auto">
				<MainFooter customMarginBottom="40px" />
			</StyledBox>
		</PageWrapperLayout>
	);
}
